<template>
  <div class="flex" :class="messageClass">
    <!-- AI Message -->
    <template v-if="message.sender_type === SenderType.Agent">
      <div class="flex items-end">
        <div
          v-if="messageTextContent"
          class="chat-message max-w-[85%] rounded-lg rounded-bl-none py-2"
          @mouseover="copyButtonText = messageTextContent"
          @mouseleave="copyButtonText = ''"
        >
          <markdown-text
            class="font-sans text-gray-800"
            :content="messageTextContent"
            @click="handleClick"
          />
          <div class="mt-1 h-2 w-5">
            <copy-to-clipboard-button
              v-if="copyButtonText"
              :show-tooltip="false"
              :value="stripHTMLTagsFromString(copyButtonText)"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- User Message -->
    <template v-else>
      <div
        @mouseover="copyButtonText = messageTextContent"
        @mouseleave="copyButtonText = ''"
        class="flex max-w-[85%] flex-col space-y-1"
      >
        <div
          v-if="messageTextContent.length"
          class="chat-message rounded-lg rounded-br-none bg-primary-background-active px-4 py-2 whitespace-pre-wrap text-headline"
        >
          <markdown-text
            class="font-sans"
            :content="messageTextContent"
            @click="handleClick"
          />
        </div>
        <div class="h-2 w-5">
          <copy-to-clipboard-button
            v-if="copyButtonText"
            :show-tooltip="false"
            :value="stripHTMLTagsFromString(copyButtonText)"
          />
        </div>
        <div v-if="files.length" class="flex flex-wrap gap-2 p-3">
          <div
            v-for="(file, index) in files"
            :key="index"
            class="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
            data-cy="uploaded-file-item"
          >
            <icon-base
              :icon="getFileIcon(file.file_name)"
              class="flex-shrink-0"
            />
            <span class="text-sm break-all text-gray-700">
              {{ file.file_name }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { SenderType } from "@/enums/ai";
import { stripHTMLTagsFromString } from "@/helpers/common";
import { AI_AGENTS_REGEX } from "@/helpers/constants/aiAgents";
import { getFileIcon } from "@/helpers/files";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";

import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";
import MarkdownText from "@/components/ui/text/MarkdownText.vue";

import type { AiAgentSessionMessage } from "@/models/aiAgents";

const { message } = defineProps<{
  message: AiAgentSessionMessage;
}>();

const router = useRouter();

const copyButtonText = ref("");

const messageTextContent = computed(() => message?.message ?? "");

const files = computed(
  () => message?.content?.content?.filter((item) => item.type === "file") ?? []
);

const messageClass = computed(() => {
  if (message.sender_type === SenderType.User) {
    return "justify-end";
  }

  return "justify-start";
});

const handleClick = (e: MouseEvent) => {
  const target = e.target;

  const isLendflowDealLink =
    target instanceof HTMLAnchorElement &&
    target.href.includes("lendflow") &&
    AI_AGENTS_REGEX.DEAL.test(target.href);

  /**
   * When it's a Lendflow deal link, we want to redirect
   * the user to this deal without reloading the page
   */

  if (isLendflowDealLink) {
    e.preventDefault();

    const dealId = AI_AGENTS_REGEX.DEAL.exec(target.href)?.[1];

    if (!dealId) {
      return;
    }

    router.push({
      name: ROUTE_DEAL_PROGRESS,
      params: { id: dealId },
    });
  }
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep(.chat-message) a {
  @apply text-primary underline;
}
</style>
